/**
 * setStrokeStrip — what picture one Sets-tab row draws of its set.
 *
 * Scott, 2026-09-11, on his phone: "on the sets you should show what the set
 * actually is visually instead of having to click it to see the colors. You
 * should be able to see it and understand the strokes from the main sets tab."
 *
 * Input is the set as LAST swum, run-length encoded in SWIM ORDER
 * (swimFacts v5 `sets[].blocks`; see allTimeAggregates.strokeRuns). The order
 * is the whole point: a per-stroke aggregate would draw The Butterfly Sandwich
 * as fly-fly-breast-breast-free-free-free-free, an order it never swam and one
 * its own name contradicts.
 *
 * Three shapes, one rule each (Scott ruled "both: tiles, bar as fallback for
 * 12+ lengths" on the mock):
 *
 *   band  — one stroke, any length: "20 × FREE". A wall of identical tiles
 *           reads as a render bug; the band reads as one stroke, N lengths
 *           (the share card's chipGrid rule, and SetGroupRibbon's singleBand).
 *   tiles — mixed, under BAR_MIN_LENGTHS: one tile per length, in swim order,
 *           each in its stroke's colour, carrying its stroke word while the
 *           words still fit.
 *   bar   — mixed, BAR_MIN_LENGTHS or more: the same one-cell-per-length row,
 *           smaller and wordless, so a long set still counts.
 *
 * Any row whose cells are too small for words carries a CAPTION instead, and
 * the caption is then the only text: "FREE 6 · FLY 2 · BACK 2 · BREAST 2".
 * The bar has carried, in turn, no key at all (VP round 1: 0 of 5 readers could
 * name a stroke once the top-of-list legend scrolled away), then words on the
 * segments that fit them (round 2: arbitrary — FLY printed where BREAST could
 * not), then an abbreviation tier (round 3: "BRST" on the bar against "BREAST"
 * in its own caption three inches below, 5/5). Words belong on tiles big enough
 * for the whole word; everywhere else the caption says it once, in the app's
 * own vocabulary.
 *
 * And the bar draws ONE CELL PER LENGTH with a visible gutter between cells —
 * not runs. Merging same-stroke lengths into a block hid the count the feature
 * exists to show: 0 of 5 readers could count 12 in a 9-block bar, and 5/5 read
 * the bar as contradicting its own caption (round 3).
 *
 * Pure module. The view owns pixels; this owns the rules.
 */
import type { SwimFactsSetBlock } from "./allTimeAggregates";
import { strokeWord } from "./designTokens";

/** A mixed set this long draws the proportional bar instead of tiles (Scott,
 *  2026-09-11 on the mock: tiles until 12 lengths, bar from 12). */
export const BAR_MIN_LENGTHS = 12;

/** Tiles carry their stroke word up to this many lengths, colour only above it.
 *  At 390 px the card gives the strip 318 px, so 8 tiles sit on a ~37 px pitch
 *  and BREAST — the longest word — needs ~36 px at the tile's 9 px bold caps.
 *  A ninth tile takes the pitch under the word. */
export const TILE_WORD_MAX = 8;

export type SetStrokeStrip =
  /** One stroke, any length: "20 × FREE". */
  | { kind: "band"; stroke: string; lengths: number; label: string }
  /** One tile per length, in swim order; `caption` when they are wordless. */
  | { kind: "tiles"; tiles: string[]; labelled: boolean; caption: string | null }
  /** One cell per length, in swim order, wordless; the caption is the text. */
  | { kind: "bar"; cells: string[]; caption: string };

export interface StripContext {
  /** The set's meters in the swim being drawn — passed ONLY when the row's
   *  header shows a distance RANGE, where a bare "20 × FREE" left readers
   *  doing arithmetic against "50–575 m" (VP round 1, B2). A fixed-distance
   *  set's header already says the distance, so it passes nothing. */
  rangeMeters?: number | null;
}

/** The picture for one set, or null when the set has no lengths to draw (a
 *  swim cached before v5 carries no blocks until the background rescan). */
export function strokeStripFor(
  blocks: readonly SwimFactsSetBlock[],
  ctx: StripContext = {},
): SetStrokeStrip | null {
  const runs = blocks.filter((b) => b.n > 0);
  const total = runs.reduce((n, b) => n + b.n, 0);
  if (total === 0) return null;

  const strokes = new Set(runs.map((b) => b.stroke));
  if (strokes.size === 1) {
    const stroke = runs[0].stroke;
    const word = strokeWord(stroke);
    // "1 × FREE" is a count nobody says out loud; one length is just the word.
    const count = total === 1 ? word : `${total} × ${word}`;
    const meters = ctx.rangeMeters;
    return {
      kind: "band",
      stroke,
      lengths: total,
      label: meters ? `${count} · ${meters} m` : count,
    };
  }
  const cells = runs.flatMap((b) => Array.from({ length: b.n }, () => b.stroke));
  if (total >= BAR_MIN_LENGTHS) {
    return { kind: "bar", cells, caption: captionFor(runs) };
  }
  const labelled = total <= TILE_WORD_MAX;
  return { kind: "tiles", tiles: cells, labelled, caption: labelled ? null : captionFor(runs) };
}

/** "FREE 6 · FLY 2 · BACK 2 · BREAST 2" — every stroke in the row with its
 *  share of the lengths, in the order the swim first reaches it, so the line
 *  reads in the same direction as the colour above it. This is a wordless
 *  row's only text, and it uses the app's own stroke words (designTokens.
 *  strokeWord), the same ones the tiles wear — never an abbreviation of its
 *  own (VP round 3: "BRST" on the bar against "BREAST" in this very line). */
export function captionFor(runs: readonly SwimFactsSetBlock[]): string {
  const order: string[] = [];
  const lengths = new Map<string, number>();
  for (const run of runs) {
    if (!lengths.has(run.stroke)) order.push(run.stroke);
    lengths.set(run.stroke, (lengths.get(run.stroke) ?? 0) + run.n);
  }
  return order.map((stroke) => `${strokeWord(stroke)} ${lengths.get(stroke)}`).join(" · ");
}
